import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { HERO } from "../../data/content";
import { MaskedLines } from "../motion/Motion";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-brand-primary"
    >
      {/* Parallax background */}
      <motion.div style={{ y }} className="absolute inset-0 h-[120%] w-full">
        <img
          src={HERO.image}
          alt="Modern családi ház hőszigeteléssel"
          className="h-full w-full object-cover"
          loading="eager"
        />
      </motion.div>

      {/* Overlays */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-gradient-to-r from-brand-primary via-brand-primary/80 to-brand-primary/30"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-primary/90 via-transparent to-brand-primary/40" />

      {/* Content grid */}
      <motion.div
        style={{ y: contentY }}
        className="relative z-10 mx-auto grid min-h-[100svh] max-w-[1400px] grid-cols-1 items-center gap-10 px-5 pt-24 pb-24 sm:px-6 md:px-12 md:pt-28 md:pb-32 lg:grid-cols-12 lg:gap-12"
      >
        {/* Left: text content */}
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-6 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-brand-accent"
          >
            <span className="h-px w-10 bg-brand-accent" />
            {HERO.overline}
          </motion.p>

          <h1 className="font-heading text-[1.95rem] font-extrabold leading-[1.06] tracking-tight text-white sm:text-5xl md:text-6xl lg:text-[4.25rem] max-w-4xl">
            <MaskedLines lines={HERO.titleLines} delay={0.15} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-6 max-w-xl text-[0.95rem] leading-relaxed text-white/75 sm:text-base md:mt-8 md:text-lg"
          >
            {HERO.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-10 flex flex-col gap-3 sm:flex-row sm:gap-4"
          >
            <button
              data-testid="hero-primary-cta"
              onClick={() => scrollTo("#kapcsolat")}
              className="group flex items-center justify-center gap-2 bg-brand-accent px-8 py-4 font-semibold text-white transition-all duration-300 hover:bg-brand-accentDark"
            >
              {HERO.primaryCta}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => scrollTo("#szolgaltatasok")}
              className="flex items-center justify-center gap-2 border border-white/40 px-8 py-4 font-semibold text-white transition-all duration-300 hover:bg-white hover:text-brand-primary"
            >
              {HERO.secondaryCta}
            </button>
          </motion.div>

          {/* Trust badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-6 md:mt-14"
          >
            {HERO.trust.map((item) => (
              <div key={item} className="flex items-center gap-2.5 text-sm text-white/85">
                <span className="grid h-6 w-6 place-items-center bg-brand-accent/20 text-brand-accent">
                  <Check size={14} strokeWidth={3} />
                </span>
                {item}
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right: Wistia video */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
          data-testid="hero-video"
          className="relative mx-auto w-full max-w-[320px] lg:col-span-5 lg:mx-0 lg:ml-auto lg:max-w-[400px]"
        >
          {/* decorative offset block */}
          <span
            aria-hidden="true"
            className="absolute -bottom-4 -right-4 hidden h-24 w-24 bg-brand-accent md:block"
          />
          <span
            aria-hidden="true"
            className="absolute -left-3 -top-3 h-16 w-16 border border-brand-accent/60 md:h-20 md:w-20"
          />
          {/* frame */}
          <div className="relative overflow-hidden border-4 border-white/95 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.55)]">
            <div
              className="w-full"
              dangerouslySetInnerHTML={{
                __html:
                  '<wistia-player media-id="9sz28ulslz" aspect="0.5625" style="display:block;width:100%;"></wistia-player>',
              }}
            />
          </div>
          <div className="mt-3 flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.25em] text-white/60">
            <span className="h-px w-6 bg-brand-accent" />
            Bemutatkozó videó
          </div>
        </motion.div>
      </motion.div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3 }}
        className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 md:flex"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
          Görgessen
        </span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
          className="h-10 w-px bg-white/40"
        />
      </motion.div>
    </section>
  );
}
